import { Box, Divider, FormField, Input, SidePanel, Text } from "@wix/design-system";
import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ModalServiceContext, RicosContext, usePluginExportsContext } from "./../../ricos-context";
import { useBiNext } from "./../../_shared/use-bi-next";
import { useNodeAttrs } from "./../../_shared/use-node-attrs";
import { useSpoilerPlugin } from "./../../_shared/use-spoiler-plugin";
import {
  ImageNew as Image,
  ToggleButton,
  UiComponentsProvider,
  useClickOutside
} from "./../../ui-components";
import { imageModals } from "../consts";
import {
  SETTINGS_ALT_INPUT_TEXT,
  SETTINGS_DISABLE_DOWNLOAD_TOGGLE,
  SETTINGS_DISABLE_EXPAND_TOGGLE,
  SETTINGS_IMAGE_PREVIEW,
  SETTINGS_ROOT,
  SETTINGS_SPOILER_TOGGLE
} from "../dataHooks";
import { IMAGE_TYPE } from "../types";
import classes from "./settings-scss";
const Settings = ({ nodeId }) => {
  const { t, environment, isMobile } = useContext(RicosContext);
  const [modalRef, setModalRef] = useState(null);
  const containerRef = useRef(modalRef);
  const modalService = useContext(ModalServiceContext) || {};
  const exports = usePluginExportsContext();
  const { pmNodeAttrs, patchPmNodeAttrs } = useNodeAttrs(nodeId, "IMAGE");
  const logBi = useBiNext();
  if (!pmNodeAttrs) {
    console.error("Image settings: node attrs not found");
    return null;
  }
  const isSpoilerEnabled = useSpoilerPlugin(IMAGE_TYPE);
  const AltTextComponent = exports?.aiIntegrations?.image?.altTextInput;
  const altText = pmNodeAttrs.altText ?? "";
  useEffect(() => {
    if (modalRef) {
      containerRef.current = modalRef;
    }
  }, [modalRef]);
  useClickOutside({
    containerRef,
    onClickOutside: () => {
      modalService.closeModal(imageModals.settings);
    },
    triggerOn: "pointerdown"
  });
  return /* @__PURE__ */ React.createElement(UiComponentsProvider, null, /* @__PURE__ */ React.createElement("div", { className: classes.modal, tabIndex: -1, ref: setModalRef }, /* @__PURE__ */ React.createElement(
    SidePanel,
    {
      onCloseButtonClick: () => modalService.closeModal(imageModals.settings),
      dataHook: classNames("floating-layout-root", SETTINGS_ROOT),
      skin: "floating",
      width: isMobile ? "100%" : 288,
      height: isMobile ? "100vh" : void 0
    },
    /* @__PURE__ */ React.createElement("div", { "data-drag-handler": "true" }, /* @__PURE__ */ React.createElement(
      SidePanel.Header,
      {
        showDivider: true,
        className: classes.header,
        title: t("ImageSettings_Header")
      }
    )),
    modalRef && /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3", padding: "SP3" }, /* @__PURE__ */ React.createElement(
      Image,
      {
        dataHook: SETTINGS_IMAGE_PREVIEW,
        alt: altText || t("ImagePlugin_Image_Preview_Alt"),
        src: pmNodeAttrs.image?.src?.id || pmNodeAttrs.image?.src?.url || "",
        width: Number(pmNodeAttrs.image?.width),
        height: Number(pmNodeAttrs.image?.height),
        requiredHeight: 156,
        requiredWidth: 252,
        borderRadius: environment === "businessManager" ? 8 : 0
      }
    ), AltTextComponent ? /* @__PURE__ */ React.createElement(
      AltTextComponent,
      {
        image: pmNodeAttrs.image,
        altText,
        onChange: (value) => {
          patchPmNodeAttrs({ altText: value });
        },
        tooltipAppendTo: modalRef
      }
    ) : /* @__PURE__ */ React.createElement(
      FormField,
      {
        label: t("ImageSettings_Alt_Label"),
        infoContent: /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP1" }, /* @__PURE__ */ React.createElement(Text, { light: true, size: "small" }, t("ImageSettings_Alt_Label_Tooltip"))),
        infoTooltipProps: {
          size: "small",
          appendTo: modalRef
        }
      },
      /* @__PURE__ */ React.createElement(
        Input,
        {
          dataHook: SETTINGS_ALT_INPUT_TEXT,
          type: "text",
          placeholder: t("ImageSettings_Alt_Input_Placeholder"),
          value: altText,
          onChange: (e) => {
            patchPmNodeAttrs({ altText: e.target.value });
          },
          size: "small",
          textOverflow: "ellipsis"
        }
      )
    ), /* @__PURE__ */ React.createElement(Box, { marginInline: "-18px" }, /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, t("ImagePlugin_Settings_Section_Options")), /* @__PURE__ */ React.createElement(
      ToggleButton,
      {
        title: t("ImagePlugin_Settings_ImageOpensInExpandMode_Label"),
        checked: !pmNodeAttrs.disableExpand,
        onChange: () => {
          logBi({ dataHook: SETTINGS_DISABLE_EXPAND_TOGGLE });
          patchPmNodeAttrs({
            disableExpand: !pmNodeAttrs.disableExpand
          });
        },
        infoContent: t("ImageSettings_Expand_Mode_Toggle"),
        dataHook: SETTINGS_DISABLE_EXPAND_TOGGLE,
        size: "small",
        tooltipAppendTo: modalRef
      }
    ), /* @__PURE__ */ React.createElement(
      ToggleButton,
      {
        title: t("ImagePlugin_Settings_ImageCanBeDownloaded_Label"),
        checked: !pmNodeAttrs.disableDownload,
        onChange: () => {
          logBi({ dataHook: SETTINGS_DISABLE_DOWNLOAD_TOGGLE });
          patchPmNodeAttrs({
            disableDownload: !pmNodeAttrs.disableDownload
          });
        },
        infoContent: t("ImagePlugin_Settings_ImageCanBeDownloaded_Tooltip"),
        dataHook: SETTINGS_DISABLE_DOWNLOAD_TOGGLE,
        size: "small",
        tooltipAppendTo: modalRef
      }
    ), isSpoilerEnabled ? /* @__PURE__ */ React.createElement(
      ToggleButton,
      {
        title: t("ImageSettings_Spoiler_Toggle"),
        checked: Boolean(pmNodeAttrs.containerData?.spoiler?.enabled),
        onChange: () => {
          logBi({ dataHook: SETTINGS_SPOILER_TOGGLE });
          patchPmNodeAttrs({
            containerData: {
              ...pmNodeAttrs.containerData,
              spoiler: {
                ...pmNodeAttrs.containerData?.spoiler,
                enabled: !pmNodeAttrs.containerData?.spoiler?.enabled
              }
            }
          });
        },
        infoContent: t("Spoiler_Toggle_Tooltip"),
        dataHook: SETTINGS_SPOILER_TOGGLE,
        size: "small",
        tooltipAppendTo: modalRef
      }
    ) : null)
  )));
};
export {
  Settings
};
//# sourceMappingURL=settings.js.map
